import {Amplify} from "aws-amplify";
import {fetchAuthSession, signOut} from "aws-amplify/auth";
import {Link, Outlet, useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ButtonBase from "@mui/material/ButtonBase";
import type React from "react";
import {useEffect, useState} from "react";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import FlagIcon from "@mui/icons-material/Flag";
import ReceiptIcon from "@mui/icons-material/Receipt";
import MailIcon from "@mui/icons-material/Mail";
import ListAltIcon from "@mui/icons-material/ListAlt";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import SettingsIcon from "@mui/icons-material/Settings";
import AssessmentIcon from "@mui/icons-material/Assessment";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DE from "country-flag-icons/react/3x2/DE";
import GB from "country-flag-icons/react/3x2/GB";
import i18n from "../i18n";
import appIcon from "../assets/app-icon.png";
import {useAppSelector} from "../store/hooks";
import {colors, fonts, layout} from "../theme/tokens";

const E2E_TEST_REPORTS_BASE = "https://calendall-e2e-test-reports-";
const E2E_TEST_REPORTS_SUFFIX = ".s3.eu-central-1.amazonaws.com/index.html";

function getStage(): string {
    const config = Amplify.getConfig() as {Custom?: Record<string, string>};
    return config.Custom?.["stage"] ?? "dev";
}

function getE2ETestResultsUrl(): string {
    return `${E2E_TEST_REPORTS_BASE}${getStage()}${E2E_TEST_REPORTS_SUFFIX}`;
}

/** Stages carry an account suffix (`dev-706971220023`); the environment name is the part before it. */
function getEnvironmentName(): string {
    return getStage().split("-")[0];
}

const FLAG_STYLE_MENU: React.CSSProperties = {width: 28, height: 19};

const LANGUAGES: {code: string; label: string; Flag: React.ComponentType<React.SVGAttributes<SVGElement>>}[] = [
    {code: "de", label: "Deutsch", Flag: DE},
    {code: "en", label: "English", Flag: GB},
];

type NavItem = {
    to: string;
    labelKey: string;
    Icon: React.ElementType;
    showTenantCount?: boolean;
};

type NavGroup = {labelKey: string; items: NavItem[]};

const NAV_GROUPS: NavGroup[] = [
    {
        labelKey: "NAV_GROUP_OPERATE",
        items: [
            {to: "/", labelKey: "NAV_DASHBOARD", Icon: DashboardIcon},
            {to: "/tenants", labelKey: "NAV_TENANTS", Icon: PeopleIcon, showTenantCount: true},
        ],
    },
    {
        labelKey: "NAV_GROUP_CONFIGURE",
        items: [
            {to: "/feature-flags", labelKey: "NAV_FEATURE_FLAGS", Icon: FlagIcon},
            {to: "/invoices", labelKey: "NAV_INVOICES", Icon: ReceiptIcon},
            {to: "/sent-emails", labelKey: "NAV_SENT_EMAILS", Icon: MailIcon},
            {to: "/plans", labelKey: "NAV_PLANS", Icon: ListAltIcon},
            {to: "/promos", labelKey: "NAV_PROMOTIONS", Icon: LocalOfferIcon},
            {to: "/support", labelKey: "NAV_SUPPORT", Icon: SupportAgentIcon},
            {to: "/settings", labelKey: "NAV_SETTINGS", Icon: SettingsIcon},
        ],
    },
];

const navItemSx = {
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    mx: 1,
    my: "1px",
    px: 1.25,
    py: "7px",
    borderRadius: "4px",
    fontSize: "13px",
    justifyContent: "flex-start",
    width: "calc(100% - 16px)",
    "& > svg:first-of-type": {fontSize: 18},
} as const;

function SidebarNavItem({to, labelKey, Icon, showTenantCount, onClick}: NavItem & {onClick?: () => void}) {
    const {t} = useTranslation();
    const location = useLocation();
    const tenantCount = useAppSelector((state) => (state.tenants.status === "succeeded" ? state.tenants.ids.length : null));
    const isActive = location.pathname === to || (to !== "/" && location.pathname.startsWith(to + "/"));

    return (
            <ButtonBase
                    component={Link}
                    to={to}
                    onClick={onClick}
                    aria-current={isActive ? "page" : undefined}
                    sx={{
                        ...navItemSx,
                        ...(isActive
                                ? {backgroundColor: colors.chrome, color: "#fff", fontWeight: 500}
                                : {color: colors.body, "&:hover": {backgroundColor: colors.navHover}, "& svg": {color: colors.muted}}),
                    }}
            >
                <Icon />
                <Box component="span" sx={{flex: 1, textAlign: "left"}}>{t(labelKey)}</Box>
                {showTenantCount && tenantCount !== null && (
                        <Box component="span" sx={{fontFamily: fonts.mono, fontSize: "11px", opacity: 0.8}}>
                            {tenantCount}
                        </Box>
                )}
            </ButtonBase>
    );
}

function useSignedInInitials(): string | null {
    const [initials, setInitials] = useState<string | null>(null);
    useEffect(() => {
        let active = true;
        fetchAuthSession()
                .then((session) => {
                    const payload = session.tokens?.idToken?.payload;
                    const name = typeof payload?.name === "string" ? payload.name : undefined;
                    const email = typeof payload?.email === "string" ? payload.email : undefined;
                    if (active) setInitials(initialsFrom(name ?? email));
                })
                .catch(() => {
                    if (active) setInitials(null);
                });
        return () => {
            active = false;
        };
    }, []);
    return initials;
}

function initialsFrom(nameOrEmail: string | undefined): string | null {
    if (!nameOrEmail) return null;
    const words = nameOrEmail.split("@")[0].split(/[\s._-]+/).filter(Boolean);
    if (words.length === 0) return null;
    return words.slice(0, 2).map((word) => word[0].toUpperCase()).join("");
}

export function TenantManagementLayout() {
    const {t} = useTranslation();
    const testResultsUrl = getE2ETestResultsUrl();
    const environmentName = getEnvironmentName();
    const initials = useSignedInInitials();
    const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const currentLang = LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0];

    const closeLangMenu = () => setLangAnchorEl(null);
    const setLanguage = (code: string) => {
        void i18n.changeLanguage(code);
        closeLangMenu();
    };

    const navList = (
            <Box sx={{display: "flex", flexDirection: "column", height: "100%", backgroundColor: "#fff"}}>
                <Box sx={{flex: 1, overflowY: "auto", pt: 1.5, pb: 1}}>
                    {NAV_GROUPS.map((group, index) => (
                            <Box key={group.labelKey} sx={{mt: index > 0 ? 1.5 : 0}}>
                                <Typography variant="overline" component="div" sx={{px: 2, pb: 0.5, color: colors.faint, display: "block"}}>
                                    {t(group.labelKey)}
                                </Typography>
                                <Box component="ul" sx={{listStyle: "none", m: 0, p: 0}}>
                                    {group.items.map((item) => (
                                            <li key={item.to}>
                                                <SidebarNavItem {...item} onClick={mobileOpen ? () => setMobileOpen(false) : undefined} />
                                            </li>
                                    ))}
                                </Box>
                            </Box>
                    ))}
                </Box>
                <Box sx={{borderTop: `1px solid ${colors.cardBorder}`, py: 1}}>
                    <ButtonBase
                            component="a"
                            href={testResultsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{...navItemSx, color: colors.body, "&:hover": {backgroundColor: colors.navHover}, "& svg": {color: colors.muted}}}
                    >
                        <AssessmentIcon />
                        <Box component="span" sx={{flex: 1, textAlign: "left"}}>{t("NAV_TEST_RESULTS")}</Box>
                        <OpenInNewIcon sx={{fontSize: 14}} />
                    </ButtonBase>
                </Box>
            </Box>
    );

    const topBarDivider = <Box aria-hidden sx={{width: "1px", height: 22, backgroundColor: "rgba(255,255,255,.2)", mx: 1.5}} />;

    return (
            <Box sx={{display: "flex", flexDirection: "column", minHeight: "100vh"}}>
                <AppBar position="sticky" sx={{zIndex: (theme) => theme.zIndex.drawer + 1}}>
                    <Toolbar disableGutters sx={{minHeight: {xs: layout.topBarHeight, sm: layout.topBarHeight}, px: 2}}>
                        <IconButton
                                color="inherit"
                                edge="start"
                                onClick={() => setMobileOpen(true)}
                                sx={{mr: 1, display: {md: "none"}}}
                                aria-label="open navigation"
                        >
                            <MenuIcon />
                        </IconButton>
                        <Box component={Link} to="/" sx={{display: "flex", alignItems: "center", gap: 1, color: "inherit", textDecoration: "none"}}>
                            <Box component="img" src={appIcon} alt="" sx={{width: 22, height: 22, borderRadius: "5px"}} />
                            <Typography component="span" sx={{fontSize: "14px", fontWeight: 600, letterSpacing: ".4px"}}>
                                Calendall
                            </Typography>
                            <Typography component="span" sx={{fontSize: "14px", color: "rgba(255,255,255,.62)"}}>
                                {t("NAV_ADMIN")}
                            </Typography>
                        </Box>
                        <Box sx={{flexGrow: 1}} />
                        <Box sx={{display: {xs: "none", sm: "flex"}, alignItems: "center", gap: 0.75, fontSize: "12px", mr: 1}}>
                            <Box aria-hidden sx={{width: 7, height: 7, borderRadius: "50%", backgroundColor: "#4cd08a"}} />
                            <span title={getStage()}>{environmentName}</span>
                        </Box>
                        <Button
                                color="inherit"
                                onClick={(event) => setLangAnchorEl(event.currentTarget)}
                                size="small"
                                endIcon={<ExpandMoreIcon />}
                                aria-label={currentLang.label}
                                aria-haspopup="true"
                                aria-controls={langAnchorEl ? "lang-menu" : undefined}
                                sx={{minWidth: 0, px: 1, fontSize: "12px", fontWeight: 400, "& .MuiButton-endIcon": {ml: 0.25}}}
                        >
                            {currentLang.code.toUpperCase()}
                        </Button>
                        <Menu
                                id="lang-menu"
                                anchorEl={langAnchorEl}
                                open={Boolean(langAnchorEl)}
                                onClose={closeLangMenu}
                                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                                transformOrigin={{vertical: "top", horizontal: "right"}}
                        >
                            {LANGUAGES.map((lang) => (
                                    <MenuItem key={lang.code} onClick={() => setLanguage(lang.code)} selected={i18n.language === lang.code}>
                                        <ListItemIcon sx={{minWidth: 40}}>
                                            <lang.Flag style={FLAG_STYLE_MENU} />
                                        </ListItemIcon>
                                        <ListItemText>{lang.label}</ListItemText>
                                    </MenuItem>
                            ))}
                        </Menu>
                        {topBarDivider}
                        {initials && (
                                <Box
                                        aria-hidden
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: "50%",
                                            backgroundColor: "rgba(255,255,255,.18)",
                                            fontSize: "10.5px",
                                            fontWeight: 600,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mr: 0.5,
                                        }}
                                >
                                    {initials}
                                </Box>
                        )}
                        <Button color="inherit" onClick={() => void signOut()} size="small" sx={{fontSize: "13px", fontWeight: 400, px: 1}}>
                            {t("SIGN_OUT")}
                        </Button>
                    </Toolbar>
                </AppBar>

                <Box sx={{display: "flex", flex: 1}}>
                    <Box
                            component="nav"
                            aria-label={t("NAV_MAIN_ARIA")}
                            sx={{
                                display: {xs: "none", md: "flex"},
                                flexDirection: "column",
                                width: layout.navWidth,
                                flexShrink: 0,
                                borderRight: `1px solid ${colors.cardBorder}`,
                                position: "sticky",
                                top: layout.topBarHeight,
                                height: `calc(100vh - ${layout.topBarHeight}px)`,
                                overflowY: "auto",
                                backgroundColor: "#fff",
                            }}
                    >
                        {navList}
                    </Box>

                    <Drawer
                            variant="temporary"
                            open={mobileOpen}
                            onClose={() => setMobileOpen(false)}
                            ModalProps={{keepMounted: true}}
                            sx={{
                                display: {xs: "block", md: "none"},
                                "& .MuiDrawer-paper": {width: layout.navWidth, boxSizing: "border-box"},
                            }}
                    >
                        {navList}
                    </Drawer>

                    <Box
                            component="main"
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                backgroundColor: colors.canvas,
                                p: "16px 20px 20px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "14px",
                            }}
                    >
                        <Outlet />
                    </Box>
                </Box>
            </Box>
    );
}
